<template>
  <section id="assortiment" class="assortiment-section" aria-labelledby="assort-heading">
    <div class="max-w-screen-xl mx-auto px-6">

      <!-- Header -->
      <div class="assort-header">
        <div class="assort-header-left">
          <span class="eyebrow-pill">Ons assortiment</span>
          <h2 id="assort-heading" class="assort-title">Alles voor groene energie thuis</h2>
          <p class="assort-subtitle">Van plug&nbsp;&amp;&nbsp;play zonnepanelen tot slimme laadpalen en thuisaccu's.</p>
        </div>
        <router-link to="/producten" class="assort-all-link">
          Volledig assortiment
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
        </router-link>
      </div>

      <!-- Featured pair - grote kaarten -->
      <div class="featured-pair" role="list">
        <div
          v-for="item in categories.slice(0, 2)"
          :key="item.slug"
          class="feat-card"
          role="listitem"
        >
          <router-link :to="`/categorie/${item.slug}`" class="feat-card-inner" :aria-label="`Bekijk ${item.name}`">
            <div class="feat-img-wrap">
              <img
                :src="sized(item.image, 640)"
                :srcset="srcset(item.image, 640)"
                sizes="(max-width: 900px) 100vw, 50vw"
                :alt="item.imageAlt"
                loading="lazy"
                decoding="async"
                class="feat-img"
              />
              <div class="feat-img-overlay"></div>
            </div>
            <div class="feat-content">
              <span v-if="item.badge" class="feat-badge">{{ item.badge }}</span>
              <h3 class="feat-name">{{ item.name }}</h3>
              <ul class="feat-specs" aria-label="Specificaties">
                <li v-for="spec in item.specs" :key="spec">{{ spec }}</li>
              </ul>
              <span class="feat-cta">
                Bekijk alles
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
              </span>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Mini grid - kleinere kaarten -->
      <div class="mini-grid" role="list">
        <div
          v-for="item in categories.slice(2)"
          :key="item.slug"
          class="mini-card"
          role="listitem"
        >
          <router-link :to="`/categorie/${item.slug}`" class="mini-card-inner" :aria-label="`Bekijk ${item.name}`">
            <div class="mini-img-wrap">
              <img
                :src="sized(item.image, 360)"
                :srcset="srcset(item.image, 360)"
                sizes="(max-width: 900px) 50vw, 300px"
                :alt="item.imageAlt"
                loading="lazy"
                decoding="async"
                class="mini-img"
              />
            </div>
            <div class="mini-content">
              <div class="mini-top">
                <span class="mini-icon"><i :class="`bi ${item.icon}`" aria-hidden="true"></i></span>
                <span v-if="item.badge" class="mini-badge">{{ item.badge }}</span>
              </div>
              <h3 class="mini-name">{{ item.name }}</h3>
              <p class="mini-specs">{{ item.specs.join(' · ') }}</p>
            </div>
          </router-link>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
// Bouwt een sized bron voor interne /wp-content-afbeeldingen (via de media-proxy
// die on-the-fly verkleint + naar WebP omzet). Externe URL's laten we ongemoeid.
const sized = (url, w) => (url && url.startsWith('/wp-content')) ? `${url}?w=${w}` : url
const srcset = (url, w) => (url && url.startsWith('/wp-content'))
  ? `${url}?w=${w} 1x, ${url}?w=${w * 2} 2x` : null

const categories = [
  {
    slug: 'laadpalen',
    name: 'Laadpalen',
    badge: 'Populair',
    icon: 'bi-ev-front',
    image: '/wp-content/uploads/2026/07/Gemini_Generated_Image_j8cvjcj8cvjcj8cv.png',
    imageAlt: 'Laadpaal thuis installatie',
    specs: ['Thuisladers 3,7 – 22 kW', 'Slimme laadpalen', 'Publieke laadpalen'],
  },
  {
    slug: 'zonnepanelen',
    name: 'Zonnepanelen',
    badge: 'Bestseller',
    icon: 'bi-sun',
    image: '/wp-content/uploads/2026/07/balkonpanelen_met_batterij.png',
    imageAlt: 'Zonnepanelen op balkon',
    specs: ['Monokristallijn', 'All-black panelen', 'Complete balkonsets'],
  },
  {
    slug: 'thuisbatterijen',
    name: "Thuisaccu's",
    badge: null,
    icon: 'bi-battery-charging',
    image: 'https://nl.jackery.com/cdn/shop/files/KV4_S3_Pro_Max_AC_NL_PC.jpg?v=1774266671&width=800',
    imageAlt: 'Thuisaccu energieopslag',
    specs: ['5 – 20 kWh opslag', 'Hybride systemen'],
  },
  {
    slug: 'type-2-kabels',
    name: 'Kabels & Adapters',
    badge: null,
    icon: 'bi-plug',
    image: '/wp-content/uploads/2026/07/laadkabels-adapters.png',
    imageAlt: 'Laadkabels Type 2 en CCS',
    specs: ['Type 1 & 2', 'CCS & CHAdeMO'],
  },
  {
    slug: 'energiebeheer',
    name: 'Energiebeheer',
    badge: 'Nieuw',
    icon: 'bi-graph-up-arrow',
    image: 'https://ifdalivestorage.blob.core.windows.net/user-uploads/784198/mid-size/612738/79374134-8b6d-4891-95b9-e88e195a2bb1/612738__jackery_solar_generator_app__image___04.jpg',
    imageAlt: 'Energiebeheer dashboard',
    specs: ['Dynamische tarieven', 'Monitoring'],
  },
  {
    slug: 'batterij-accessoires',
    name: 'Installatie',
    badge: null,
    icon: 'bi-tools',
    image: '/wp-content/uploads/2026/07/Gemini_Generated_Image_j8cvjcj8cvjcj8cv.png',
    imageAlt: 'Installatie materiaal',
    specs: ['Beugels & rails', 'Kabelgoten'],
  },
]
</script>

<style scoped>
.assortiment-section {
  padding: 4.5rem 0 5rem;
  background: #fafafa;
}

/* ── Header ── */
.assort-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
}
.assort-header-left { flex: 1; min-width: 0; }

.eyebrow-pill {
  display: inline-block;
  background: rgba(255, 213, 0, 0.15);
  color: #7a5f00;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.3rem 1rem;
  border-radius: 50px;
  border: 1px solid rgba(255, 213, 0, 0.3);
  margin-bottom: 0.875rem;
}

.assort-title {
  font-size: clamp(1.625rem, 3.5vw, 2.375rem);
  font-weight: 900;
  color: #111;
  line-height: 1.12;
  letter-spacing: -0.02em;
  margin: 0 0 0.5rem;
  text-wrap: balance;
}
.assort-subtitle {
  font-size: 1rem;
  color: #666;
  line-height: 1.65;
  margin: 0;
  max-width: 480px;
}

.assort-all-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9375rem;
  color: #111;
  text-decoration: none;
  border-bottom: 2px solid #FFD500;
  padding-bottom: 2px;
  white-space: nowrap;
  transition: gap 0.2s;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 0.25rem;
}
.assort-all-link:hover { gap: 0.75rem; }

/* ── Featured pair ── */
.featured-pair {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.feat-card { border-radius: 16px; overflow: hidden; }

.feat-card-inner {
  display: block;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: #111;
}

.feat-img-wrap {
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
  /* Skeleton-shimmer tijdens lazy-load i.p.v. een vlak zwart vlak */
  background: linear-gradient(110deg, #171717 30%, #272727 50%, #171717 70%);
  background-size: 200% 100%;
  animation: p2c-skeleton 1.5s ease-in-out infinite;
}
.feat-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.feat-card-inner:hover .feat-img { transform: scale(1.05); }

.feat-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, transparent 100%);
}

.feat-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.75rem;
}

.feat-badge {
  display: inline-block;
  background: #FFD500;
  color: #111;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.2rem 0.625rem;
  border-radius: 4px;
  margin-bottom: 0.625rem;
}

.feat-name {
  font-size: clamp(1.25rem, 2.5vw, 1.875rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.15;
  margin: 0 0 0.625rem;
}

.feat-specs {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
}
.feat-specs li {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.7);
  position: relative;
  padding-left: 0.75rem;
}
.feat-specs li::before {
  content: '·';
  position: absolute;
  left: 0;
  color: #FFD500;
}

.feat-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: #FFD500;
  color: #111;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: background 0.2s, gap 0.2s;
}
.feat-card-inner:hover .feat-cta {
  background: #ffb800;
  gap: 0.625rem;
}

/* ── Mini grid ── */
.mini-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
}

.mini-card { border-radius: 12px; overflow: hidden; }

.mini-card-inner {
  display: block;
  text-decoration: none;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}
.mini-card-inner:hover {
  border-color: #FFD500;
  box-shadow: 0 6px 24px rgba(255,213,0,0.12);
  transform: translateY(-2px);
}

.mini-img-wrap {
  aspect-ratio: 4/3;
  overflow: hidden;
  /* Lichte skeleton-shimmer tijdens lazy-load */
  background: linear-gradient(110deg, #efefef 30%, #f8f8f8 50%, #efefef 70%);
  background-size: 200% 100%;
  animation: p2c-skeleton 1.5s ease-in-out infinite;
}

@keyframes p2c-skeleton {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.mini-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.mini-card-inner:hover .mini-img { transform: scale(1.06); }

.mini-content { padding: 1rem 1.125rem 1.125rem; }

.mini-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.mini-icon {
  width: 32px;
  height: 32px;
  background: #fffbeb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: #7a5f00;
}

.mini-badge {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #FFF3B0;
  color: #7a5f00;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
}

.mini-name {
  font-size: 0.9375rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 0.3rem;
  line-height: 1.3;
}

.mini-specs {
  font-size: 0.78rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

/* ── Responsive ── */
@media (max-width: 1023px) {
  .mini-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 767px) {
  .featured-pair { grid-template-columns: 1fr; }
  .mini-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
  .assort-all-link { display: none; }
  .feat-content { padding: 1.25rem; }
  .feat-specs { display: none; }
}

@media (max-width: 480px) {
  .mini-grid { grid-template-columns: repeat(2, 1fr); gap: 0.625rem; }
}

@media (prefers-reduced-motion: reduce) {
  .feat-img, .mini-img, .feat-card-inner, .mini-card-inner { transition: none; }
  .feat-img-wrap, .mini-img-wrap { animation: none; }
}
</style>
